import { translator } from "../../../translation/translator";
import type { ShowMoreForm } from "./show-more-form";
import { ShowMoreRender } from "./show-more-render";
import { bindShowMoreTransitionEnd } from "./show-more-listener";

type ShowMoreToggleSource = "bootstrap" | "user" | "attribute";

/**
 * Show More Handler (V2)
 * handleXxx のみを担当
 */
export class ShowMoreHandler {
    public form: ShowMoreForm;

    public expanded: boolean;

    public isTransitioning: boolean;

    public hasOverflow: boolean;

    public constructor(form: ShowMoreForm) {
        this.form = form;
        this.expanded = false;
        this.isTransitioning = false;
        this.hasOverflow = true;
    }

    public bootstrap(): void {
        ShowMoreRender.ensureStructure(this.form);
        ShowMoreRender.applyDuration(this.form, this.form.getDurationMs());
        ShowMoreRender.applyLines(this.form, this.form.getLines());
        this.setExpanded(this.form.hasOpenAttribute(), false, "bootstrap");
    }

    public handleToggleClick(_event: Event): void {
        this.setExpanded(!this.expanded, true, "user");
    }

    public handleRootMutations(mutations: MutationRecord[]): void {
        let shouldSyncOpen = false;
        let shouldSyncLabel = false;
        let shouldSyncDuration = false;
        let shouldSyncLines = false;

        mutations.forEach((mutation) => {
            if (mutation.type !== "attributes") return;
            const attr = mutation.attributeName || "";
            if (attr === "open") shouldSyncOpen = true;
            if (attr === "label" || attr === "open-label" || attr === "label-key" || attr === "open-label-key") {
                shouldSyncLabel = true;
            }
            if (attr === "duration") shouldSyncDuration = true;
            if (attr === "lines") shouldSyncLines = true;
        });

        if (shouldSyncDuration) {
            ShowMoreRender.applyDuration(this.form, this.form.getDurationMs());
        }

        if (shouldSyncLines) {
            ShowMoreRender.applyLines(this.form, this.form.getLines());
            this.updateClampLayout();
        }

        if (shouldSyncOpen) {
            const nextExpanded = this.form.hasOpenAttribute();
            if (nextExpanded !== this.expanded) {
                this.setExpanded(nextExpanded, false, "attribute");
                return;
            }
        }

        if (shouldSyncLabel) {
            this.syncLabelOnly();
        }
    }

    public handleLayoutChange(): void {
        if (this.isTransitioning) return;
        this.updateClampLayout();
    }

    public syncLabelOnly(): void {
        if (this.form.labelElement instanceof HTMLElement) {
            const key = this.expanded ? this.form.getOpenLabelKey() : this.form.getLabelKey();
            if (key) {
                this.form.labelElement.setAttribute("data-translate", key);
            } else {
                this.form.labelElement.removeAttribute("data-translate");
            }
        }

        const label = this.expanded ? this.resolveOpenLabel() : this.resolveClosedLabel();
        ShowMoreRender.renderLabel(this.form, label);
        ShowMoreRender.renderAria(this.form, this.expanded);

        if (this.form.isClampMode()) {
            const content = this.form.contentElement;
            if (content instanceof HTMLElement) {
                content.setAttribute("data-clamp-label", this.resolveClosedLabel());
            }
        }
    }

    public setExpanded(expanded: boolean, animate: boolean, source: ShowMoreToggleSource): void {
        if (this.isTransitioning) return;

        if (this.form.isClampMode()) {
            this.expanded = !!expanded;
            this.form.setOpenAttribute(this.expanded);

            ShowMoreRender.renderRootState(this.form, this.expanded);
            ShowMoreRender.renderAria(this.form, this.expanded);
            const prefersReducedMotion =
                typeof globalThis.matchMedia === "function" &&
                globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;

            this.isTransitioning = !!animate && !prefersReducedMotion;
            if (this.isTransitioning) {
                document.documentElement.style.setProperty("overflow-anchor", "none");
            }
            ShowMoreRender.renderClampPanel(this.form, this.expanded, animate, prefersReducedMotion, () => {
                this.isTransitioning = false;
                this.updateClampLayout();
                document.documentElement.style.removeProperty("overflow-anchor");
            });
            this.syncLabelOnly();

            if (!this.isTransitioning && source !== "user") {
                this.updateClampLayout();
            }
            this.dispatchToggleEvent(source);
            return;
        }

        this.expanded = !!expanded;
        this.form.setOpenAttribute(this.expanded);

        ShowMoreRender.renderRootState(this.form, this.expanded);
        ShowMoreRender.renderAria(this.form, this.expanded);
        this.syncLabelOnly();

        const prefersReducedMotion =
            typeof globalThis.matchMedia === "function" &&
            globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;

        this.isTransitioning = !!animate && !prefersReducedMotion;

        ShowMoreRender.renderPanel(this.form, this.expanded, animate, prefersReducedMotion);

        const panel = this.form.panelElement;
        if (this.isTransitioning && panel instanceof HTMLDivElement) {
            document.documentElement.style.setProperty("overflow-anchor", "none");
            const onEnd = () => {
                if (this.expanded) {
                    panel.style.maxHeight = "none";
                } else {
                    panel.hidden = true;
                }
                this.isTransitioning = false;
                document.documentElement.style.removeProperty("overflow-anchor");
            };
            bindShowMoreTransitionEnd(panel, onEnd);
        }

        this.dispatchToggleEvent(source);
    }

    public updateClampLayout(): void {
        if (!this.form.isClampMode()) return;

        const lines = this.form.getLines();
        this.hasOverflow = ShowMoreRender.hasClampOverflow(this.form, lines);

        ShowMoreRender.renderToggleVisibility(this.form, this.hasOverflow);

        if (!this.hasOverflow) {
            this.expanded = false;
            this.form.setOpenAttribute(false);
            ShowMoreRender.renderRootState(this.form, false);
            ShowMoreRender.renderClampState(this.form, true);
            ShowMoreRender.renderAria(this.form, false);
            this.syncLabelOnly();
            return;
        }

        ShowMoreRender.renderClampState(this.form, this.expanded);
        this.syncLabelOnly();
    }

    public dispatchToggleEvent(source: ShowMoreToggleSource): void {
        this.form.getRoot().dispatchEvent(
            new CustomEvent("show-more:toggle", {
                bubbles: true,
                detail: {
                    expanded: this.expanded,
                    source,
                },
            }),
        );
    }

    public resolveClosedLabel(): string {
        const key = this.form.getLabelKey();
        if (key && translator && typeof translator.getTranslation === "function") {
            const translated = translator.getTranslation(key);
            if (translated && translated !== key) return translated;
        }
        return this.form.getLabelText();
    }

    public resolveOpenLabel(): string {
        const key = this.form.getOpenLabelKey();
        if (key && translator && typeof translator.getTranslation === "function") {
            const translated = translator.getTranslation(key);
            if (translated && translated !== key) return translated;
        }
        return this.form.getOpenLabelText();
    }
}
